import axiosInstance from "@/config/axiosInstance";

const VET_DEMO_ENDPOINT = "https://endsofttech.com/api/vet-demo";
const VET_DEMO_REFERRAL_ENDPOINT = `${VET_DEMO_ENDPOINT}/referrals`;
/**
 * Send contact form email
 * @param {Object} formData - Contact form data
 * @param {string} formData.name - Contact name
 * @param {string} formData.email - Contact email
 * @param {string} formData.contact - Contact phone number
 * @param {string} formData.company - Company name
 * @param {string} formData.message - Message content
 * @returns {Promise<Object>}
 */
export const sendContactEmail = async (formData) => {
  const response = await axiosInstance.post("/api/contact", formData);
  return response.data;
};

/**
 * Send quote request email
 * @param {Object} formData - Quote request data
 * @param {string} formData.name - Contact name
 * @param {string} formData.email - Contact email
 * @param {string} formData.contact - Contact phone number
 * @param {string} formData.company - Company name
 * @param {string} formData.projectDetails - Project details
 * @returns {Promise<Object>}
 */
export const sendQuoteRequest = async (formData) => {
  const response = await axiosInstance.post("/api/quote", formData);
  return response.data;
};

/**
 * Send demo request
 * @param {Object} formData - Demo request form data (camelCase from the form)
 * @returns {Promise<Object>}
 */
export const sendDemoRequest = async (formData) => {
  const filledClinicBranches = (formData.clinicBranches || []).filter((branch) => {
    return (
      branch.clinicName?.trim() ||
      branch.clinicAddressLine1?.trim() ||
      branch.clinicAddressLine2?.trim() ||
      branch.clinicContactNumber?.trim() ||
      branch.veterinarianContactNumber?.trim()
    );
  });

  const payload = {
    first_name: formData.firstName,
    last_name: formData.lastName,
    email_address: formData.email,

    // primary clinic / main contact fields
    contact_number: formData.clinicContactNumber,
    clinic_contact_number: formData.clinicContactNumber,
    veterinarian_contact_number: formData.veterinarianContactNumber || "",
    clinic_name: formData.clinicName,
    clinic_address_line1: formData.clinicAddressLine1,
    clinic_address_line2: formData.clinicAddressLine2 || "",

    // branch count is separate from provided branch details
    numberOfBranches: Number(formData.numberOfBranches || 0),
    number_of_branches: Number(formData.numberOfBranches || 0),

    // dynamic optional branch details
    additional_clinics: filledClinicBranches.map((branch) => ({
      clinic_name: branch.clinicName || "",
      clinic_address_line1: branch.clinicAddressLine1 || "",
      clinic_address_line2: branch.clinicAddressLine2 || "",
      clinic_contact_number: branch.clinicContactNumber || "",
      veterinarian_contact_number: branch.veterinarianContactNumber || "",
    })),

    business_type: formData.businessType || "Veterinary Clinic",
    has_software: formData.hasSoftware,
    current_software_name: formData.currentSoftwareName || "",
    preferred_date: formData.preferredDate || "",
    preferred_time: formData.preferredTime || "",
    referral_name: formData.referralName || "",
    referral_code: formData.referralCode || "",
    turnstileToken: formData.turnstileToken || "",
  };

  const response = await axiosInstance.post(VET_DEMO_ENDPOINT, payload);
  return response.data;
};

export const validateDemoReferral = async (referralCode) => {
  const cleanReferralCode = String(referralCode || "").trim();

  if (!cleanReferralCode) {
    return {
      isValid: false,
      clinicName: "",
    };
  }

  const response = await axiosInstance.get(
    `${VET_DEMO_REFERRAL_ENDPOINT}/${encodeURIComponent(cleanReferralCode)}`
  );

  return response.data?.data || {
    isValid: false,
    clinicName: "",
  };
};

/**
 * Send job application
 * @param {FormData} formData - Job application data (includes file upload)
 * @returns {Promise<Object>}
 */
export const sendJobApplication = async (formData) => {
  const response = await axiosInstance.post("/api/job-application", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Subscribe to newsletter
 * @param {string} email - Subscriber email
 * @param {string} source - Where the subscription came from
 * @returns {Promise<Object>}
 */
export const subscribeNewsletter = async (
  email,
  source = "Website newsletter",
  turnstileToken = ""
) => {
  const response = await axiosInstance.post("/api/newsletter/subscribe", {
    email,
    source,
    turnstileToken,
  });
  return response.data;
};
